import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import '@/App.css';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';

import LandingPage from '@/pages/LandingPage';
import AuthPage from '@/pages/AuthPage';
import FeedPage from '@/pages/FeedPage';
import SubmitIdeaPage from '@/pages/SubmitIdeaPage';
import MyIdeasPage from '@/pages/MyIdeasPage';
import EditIdeaPage from '@/pages/EditIdeaPage';
import FollowingPage from '@/pages/FollowingPage';
import UserProfilePage from '@/pages/UserProfilePage';
import CommunitiesPage from '@/pages/CommunitiesPage';
import CommunityFeedPage from '@/pages/CommunityFeedPage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Setup axios defaults
axios.defaults.baseURL = API;

export const AppContext = React.createContext();

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for JWT token (email/password auth)
    const token = localStorage.getItem('token');
    
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      // Try fetching user with cookies (Google OAuth session)
      fetchUser();
    }
  }, []);

  // Remove Emergent badge
  useEffect(() => {
    const removeBadge = () => {
      const badge = document.querySelector('#emergent-badge');
      if (badge) {
        badge.remove();
      }
    };

    // Remove immediately
    removeBadge();

    // Keep checking and removing
    const interval = setInterval(removeBadge, 100);

    return () => clearInterval(interval);
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get('/auth/me', {
        withCredentials: true // Include cookies for session-based auth
      });
      setUser(response.data);
    } catch (error) {
      // User not authenticated - this is fine
      console.log('No active session');
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  const login = (token, userData) => {
    // For JWT auth (email/password), store token
    if (token) {
      localStorage.setItem('token', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
    // For Google auth, session_token is in httpOnly cookie (no localStorage)
    setUser(userData);
    toast.success('Welcome!');
  };

  const logout = async () => {
    try {
      // Call logout endpoint to clear session cookie
      await axios.post('/auth/logout', {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    }
    
    // Clear local storage and state
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    toast.success('Logged out successfully');
  };

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-surface">
        <div className="flex flex-col items-center gap-3">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-lg animate-spin" />
          <div className="text-lg font-semibold text-primaryDark">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <AppContext.Provider value={{ user, login, logout }}>
      <BrowserRouter>
        <div className="App">
          <Routes>
            <Route path="/landing" element={user ? <Navigate to="/" /> : <LandingPage />} />
            <Route path="/auth" element={user ? <Navigate to="/" /> : <AuthPage />} />
            <Route path="/" element={user ? <FeedPage /> : <Navigate to="/landing" />} />
            <Route path="/following" element={user ? <FollowingPage /> : <Navigate to="/landing" />} />
            <Route path="/communities" element={user ? <CommunitiesPage /> : <Navigate to="/landing" />} />
            <Route path="/communities/:communityId" element={user ? <CommunityFeedPage /> : <Navigate to="/landing" />} />
            <Route path="/user/:username" element={user ? <UserProfilePage /> : <Navigate to="/landing" />} />
            <Route path="/submit" element={user ? <SubmitIdeaPage /> : <Navigate to="/landing" />} />
            <Route path="/my-ideas" element={user ? <MyIdeasPage /> : <Navigate to="/landing" />} />
            <Route path="/edit/:id" element={user ? <EditIdeaPage /> : <Navigate to="/landing" />} />
          </Routes>
          <Toaster position="top-right" richColors />
        </div>
      </BrowserRouter>
    </AppContext.Provider>
  );
}

export default App;